/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import React from 'react'
import { paragraphSmall, weightRegular } from 'src/components/css'

export type StatisticsTab = 'leaders' | 'stats'

interface StatisticsTabSelectorProps {
  selected: StatisticsTab
  onSelect: (tab: StatisticsTab) => void
  disabled?: boolean
}

/**
 * StatisticsTabSelector component
 * Displays Leaders/Stats text tabs for toggling between views
 * Styled to match MembershipTypeFilter component
 */
const StatisticsTabSelector: React.FC<StatisticsTabSelectorProps> = ({
  selected,
  onSelect,
  disabled = false,
}) => {
  const tabs: { value: StatisticsTab; label: string }[] = [
    { value: 'leaders', label: 'Leaders' },
    { value: 'stats', label: 'Player Stats' },
  ]

  return (
    <div
      css={css`
        display: inline-flex;
        align-items: center;
        gap: 0px;
        background-color: transparent;
        border: 1px solid ${colors.SECONDARY_200};
        border-radius: 8px;
        opacity: ${disabled ? 0.5 : 1};
        transition: opacity 0.2s ease;
        @media (max-width: 768px) {
          width: 100%;
          margin-bottom: -8px;
        }
      `}
    >
      {tabs.map((tab, index) => {
        const isActive = selected === tab.value
        const isFirst = index === 0
        const isLast = index === tabs.length - 1

        return (
          <button
            key={tab.value}
            type='button'
            disabled={disabled}
            onClick={() => !disabled && onSelect(tab.value)}
            css={css`
              ${paragraphSmall}
              ${weightRegular}
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 8px 24px;
              border-radius: ${isFirst
                ? '8px 0 0 8px'
                : isLast
                ? '0 8px 8px 0'
                : '0'};
              background-color: ${isActive
                ? colors.SECONDARY_300
                : 'transparent'};
              border: none;
              cursor: ${disabled ? 'not-allowed' : 'pointer'};
              transition: all 0.2s ease;
              font-weight: ${isActive ? 600 : 400};
              color: ${isActive ? colors.PRIMARY : colors.NEUTRAL_50};
              @media (max-width: 768px) {
                width: 100%;
              }

              ${!disabled &&
              css`
                &:hover {
                  background-color: ${isActive
                    ? colors.SECONDARY_300
                    : colors.SECONDARY_200};
                }

                &:active {
                  transform: scale(0.98);
                }
              `}
            `}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

export default StatisticsTabSelector
